'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgChevronright, SvgGiftBox } from '@common/icon';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';
import type { AdTriggerPhase } from '@/modules/adpopcorn-ssp';
import { resolveAdTriggerNotice } from '../_utils/adTriggerNotice';
import { useLottoPool } from '../lucky-ticket/_hooks/useLottoPool';
import { useLottoState } from '../lucky-ticket/_hooks/useLottoState';
import { resolveLottoPotGauge } from '../lucky-ticket/_utils/resolveLottoPotGauge';
import { LottoPotGauge } from './LottoPotGauge';
import styles from './LottoPotGauge.module.scss';
const CARD_CLASS_NAME = 'relative overflow-hidden flex flex-col gap-12 rounded-small bg-Gray-50 p-16 focus-visible:border-Gray-300 focus-visible:bg-Gray-100 xl:border xl:border-Gray-100 xl:bg-Gray-white';
const APPLY_BUTTON_CLASS_NAME = 'px-8 py-4 font-600 text-O-550 typo-caption1 disabled:cursor-not-allowed disabled:text-Gray-400';
const TICKET_COUNT_CLASS_NAME = 'font-600 text-Gray-600 typo-caption1';
const TicketCountLabel = () => {
  const $ = _c(5);
  const {
    data
  } = useLottoState();
  let t0;
  if ($[0] !== data.appliedTicketCount) {
    t0 = <span className="font-700 text-O-550">{data.appliedTicketCount}장</span>;
    $[0] = data.appliedTicketCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== data.requiredTicketCount || $[3] !== t0) {
    t1 = <span className={TICKET_COUNT_CLASS_NAME}>{t0} /{" "}{data.requiredTicketCount}장</span>;
    $[2] = data.requiredTicketCount;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const PoolPotGauge = t0 => {
  const $ = _c(9);
  const {
    showTicketCount,
    isCelebrating
  } = t0;
  const {
    data
  } = useLottoPool();
  const t1 = data.potGauge;
  let t2;
  if ($[0] !== data.accumulatedAmount) {
    t2 = data.accumulatedAmount.toLocaleString();
    $[0] = data.accumulatedAmount;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${t2}원`;
  let t4;
  if ($[2] !== showTicketCount) {
    t4 = showTicketCount ? <TicketCountLabel /> : undefined;
    $[2] = showTicketCount;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== data.potGauge || $[5] !== isCelebrating || $[6] !== t3 || $[7] !== t4) {
    t5 = <LottoPotGauge {...t1} accumulatedAmountLabel={t3} leading={t4} isCelebrating={isCelebrating} />;
    $[4] = data.potGauge;
    $[5] = isCelebrating;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};

//* 응모 가능 여부는 auth 쿼리라 게스트가 마운트하면 401 → 로그인 하드 리다이렉트다.
//* 회원·허브에서만 이 변형을 쓴다.
const MemberPoolPotGauge = t0 => {
  const $ = _c(3);
  const {
    showTicketCount
  } = t0;
  const {
    data
  } = useLottoState();
  let t1;
  if ($[0] !== data.canApply || $[1] !== showTicketCount) {
    t1 = <PoolPotGauge showTicketCount={showTicketCount} isCelebrating={data.canApply} />;
    $[0] = data.canApply;
    $[1] = showTicketCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

//* 반복 줄무늬는 쓰지 않는다 — 주기가 짧으면 결이 아니라 격자로 보여 눈이 아프다.
//* 카드보다 넓은 그라디언트 하나가 아주 천천히 좌우로 흐르기만 한다: 경계가 없고
//* 들어왔다 나가는 것도 아니라 '표면의 광택'으로만 읽힌다.
const FEVER_SHEEN_CLASS_NAME = 'pointer-events-none absolute inset-0 opacity-40 mix-blend-screen bg-gradient-to-r from-transparent via-Gray-white to-transparent bg-[length:220%_100%]';

/**
 * 응모 가능 상태의 카드 배경. 불투명 틴트라 알파 합성이 없고(회색 위 알파는 팔레트 밖
 * 색을 만든다), 그 위의 포일은 흰빛·Y-100을 screen으로 더하기만 해 탁해질 수 없다.
 * 카드 본문보다 **앞에** 렌더해야 텍스트가 포일 위에 남는다(대비 유지).
 */
const FeverBackdrop = () => {
  const $ = _c(2);
  const {
    data
  } = useLottoState();
  if (!data.canApply) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span aria-hidden={true} className="pointer-events-none absolute inset-0 bg-O-25" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <>{t0}<span aria-hidden={true} className={cn(FEVER_SHEEN_CLASS_NAME, styles.feverSheen)} /></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PoolPotGaugeFallback = t0 => {
  const $ = _c(5);
  const {
    showTicketCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = resolveLottoPotGauge({
      accumulatedAmount: 0,
      targetAmount: 0
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== showTicketCount) {
    t2 = showTicketCount ? <span className={TICKET_COUNT_CLASS_NAME}>-</span> : undefined;
    $[1] = showTicketCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <LottoPotGauge {...t1} accumulatedAmountLabel="-" leading={t2} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const LottoApplyButton = t0 => {
  const $ = _c(3);
  const {
    onApply,
    adTriggerPhase
  } = t0;
  const {
    data
  } = useLottoState();
  const t1 = !data.canApply || adTriggerPhase !== "ready";
  let t2;
  if ($[0] !== onApply || $[1] !== t1) {
    t2 = <button type="button" onClick={onApply} disabled={t1} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
    $[0] = onApply;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const LottoApplyTrailing = t0 => {
  const $ = _c(7);
  const {
    isGuest,
    onApply,
    adTriggerPhase
  } = t0;
  if (isGuest) {
    let t1;
    if ($[0] !== onApply) {
      t1 = <button type="button" onClick={onApply} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
      $[0] = onApply;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <button type="button" disabled={true} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" disabled={true} className={APPLY_BUTTON_CLASS_NAME}>응모하기</button>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== adTriggerPhase || $[5] !== onApply) {
    t3 = <ErrorBoundary fallback={t1}><Suspense fallback={t2}><LottoApplyButton onApply={onApply} adTriggerPhase={adTriggerPhase} /></Suspense></ErrorBoundary>;
    $[4] = adTriggerPhase;
    $[5] = onApply;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const LottoCardChevron = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SvgChevronright className="size-16 text-Gray-400 transition group-focus-visible:translate-x-1 group-focus-visible:text-Gray-600 xl:group-hover:translate-x-1 xl:group-hover:text-Gray-600" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface LuckyLottoCardProps {
  isGuest: boolean;
  //* 제공하면 허브 변형(응모하기 버튼). 미제공하면 홈 변형(카드 전체가 허브 링크).
  onApply?: () => void;
  //* 허브 변형의 응모 광고 phase — pending/cooldown 동안 설명 줄이 안내 문구로 바뀌고 버튼이 잠긴다.
  adTriggerPhase?: AdTriggerPhase;
}

/**
 * 혜택홈·행운권 허브가 공유하는 단일 '행운의 로또' 카드.
 * 누적 상금 게이지는 useLottoPool(공동 값), 응모권 수는 useLottoState에서 파생한다.
 */
export const LuckyLottoCard = t0 => {
  const $ = _c(31);
  const {
    isGuest,
    onApply,
    adTriggerPhase: t1
  } = t0;
  const adTriggerPhase = t1 === undefined ? "ready" : t1;
  const isHub = onApply !== undefined;
  const showTicketCount = isHub && !isGuest;
  let t2;
  if ($[0] !== showTicketCount) {
    t2 = showTicketCount ? <ErrorBoundary fallback={null}><Suspense fallback={null}><FeverBackdrop /></Suspense></ErrorBoundary> : null;
    $[0] = showTicketCount;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SvgGiftBox className="size-24 shrink-0" originalFill={true} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="font-700 text-Gray-900 typo-body4">행운의 로또</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== adTriggerPhase) {
    t5 = resolveAdTriggerNotice(adTriggerPhase) ?? "\uBAA9\uD45C\uAE08\uC561\uC774 \uCC44\uC6CC\uC9C0\uBA74 \uC751\uBAA8\uD560 \uC218 \uC788\uC5B4\uC694";
    $[4] = adTriggerPhase;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <div className="flex flex-1 flex-col">{t4}<p className="break-keep font-600 text-Gray-600 typo-caption1">{t5}</p></div>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== adTriggerPhase || $[9] !== isGuest || $[10] !== onApply) {
    t7 = onApply ? <LottoApplyTrailing isGuest={isGuest} onApply={onApply} adTriggerPhase={adTriggerPhase} /> : <LottoCardChevron />;
    $[8] = adTriggerPhase;
    $[9] = isGuest;
    $[10] = onApply;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="relative flex items-center gap-16">{t3}{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== showTicketCount) {
    t9 = <PoolPotGaugeFallback showTicketCount={showTicketCount} />;
    $[15] = showTicketCount;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== showTicketCount) {
    t10 = <Suspense fallback={<PoolPotGaugeFallback showTicketCount={showTicketCount} />}>{showTicketCount ? <MemberPoolPotGauge showTicketCount={showTicketCount} /> : <PoolPotGauge showTicketCount={showTicketCount} />}</Suspense>;
    $[17] = showTicketCount;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <ErrorBoundary fallback={t9}>{t10}</ErrorBoundary>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t2 || $[24] !== t8) {
    t12 = <>{t2}{t8}{t11}</>;
    $[22] = t11;
    $[23] = t2;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const body = t12;
  if (isHub) {
    let t13;
    if ($[26] !== body) {
      t13 = <div className={CARD_CLASS_NAME}>{body}</div>;
      $[26] = body;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    return t13;
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn(CARD_CLASS_NAME, "group transition-colors active:bg-Gray-100 xl:hover:border-Gray-300 xl:hover:bg-Gray-100");
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== body) {
    t14 = <CustomLink href={ROUTES.BENEFIT_LUCKY_TICKET.pathname} className={t13}>{body}</CustomLink>;
    $[29] = body;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};